import { useState, useRef, useCallback } from 'react';
import type { Contact, Group } from '@/types';
import { normalizeBrazilianPhone, BRAZIL_DDDS, DEFAULT_DDD } from '@/lib/brazilDDDs';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (contacts: Contact[]) => Promise<void>;
  existingContacts: Contact[];
  groups: Group[];
}

interface ParsedRow {
  nome: string;
  telefone: string;
  email: string;
  empresa: string;
  cidade: string;
  estado: string;
  tags: string;
  grupo: string;
  _raw: string;
}

interface ValidationResult {
  row: ParsedRow;
  index: number;
  valid: boolean;
  errors: string[];
  isDuplicate: boolean;
}

type ImportStep = 'upload' | 'preview' | 'config' | 'importing' | 'done';

function isValidBrazilianPhone(phone: string): boolean {
  const cleaned = phone.replace(/[\s\-\(\)\+]/g, '');
  return /^(\d{10,11}|\d{12,13})$/.test(cleaned);
}

function isValidInternationalPhone(phone: string): boolean {
  const cleaned = phone.replace(/[\s\-\(\)\+]/g, '');
  return /^\d{7,15}$/.test(cleaned);
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"' && !inQuotes) {
      inQuotes = true;
    } else if (ch === '"' && inQuotes) {
      if (i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = false;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

function detectDelimiter(firstLine: string): ',' | ';' | '\t' {
  const commas = (firstLine.match(/,/g) || []).length;
  const semicolons = (firstLine.match(/;/g) || []).length;
  const tabs = (firstLine.match(/\t/g) || []).length;
  if (semicolons > commas && semicolons > tabs) return ';';
  if (tabs > commas && tabs > semicolons) return '\t';
  return ',';
}

function parseCSV(text: string): ParsedRow[] {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  const delimiter = detectDelimiter(lines[0]);
  const headerLine = lines[0];
  const headers = delimiter === ',' ? parseCSVLine(headerLine) : headerLine.split(delimiter);
  const normalizedHeaders = headers.map((h) => h.trim().toLowerCase().replace(/^["']|["']$/g, ''));

  const nameIdx = normalizedHeaders.findIndex((h) => h === 'nome' || h === 'name');
  const phoneIdx = normalizedHeaders.findIndex((h) => h === 'telefone' || h === 'phone' || h === 'celular' || h === 'whatsapp');
  const emailIdx = normalizedHeaders.findIndex((h) => h === 'email' || h === 'e-mail');
  const companyIdx = normalizedHeaders.findIndex((h) => h === 'empresa' || h === 'company' || h === 'company_name');
  const cityIdx = normalizedHeaders.findIndex((h) => h === 'cidade' || h === 'city');
  const stateIdx = normalizedHeaders.findIndex((h) => h === 'estado' || h === 'state' || h === 'uf');
  const tagsIdx = normalizedHeaders.findIndex((h) => h === 'tags' || h === 'tag' || h === 'etiquetas');
  const groupIdx = normalizedHeaders.findIndex((h) => h === 'grupo' || h === 'group');

  const rows: ParsedRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const cols = delimiter === ',' ? parseCSVLine(line) : line.split(delimiter);
    const cleanCols = cols.map((c) => c.trim().replace(/^["']|["']$/g, ''));
    rows.push({
      nome: nameIdx >= 0 ? cleanCols[nameIdx] || '' : '',
      telefone: phoneIdx >= 0 ? cleanCols[phoneIdx] || '' : '',
      email: emailIdx >= 0 ? cleanCols[emailIdx] || '' : '',
      empresa: companyIdx >= 0 ? cleanCols[companyIdx] || '' : '',
      cidade: cityIdx >= 0 ? cleanCols[cityIdx] || '' : '',
      estado: stateIdx >= 0 ? cleanCols[stateIdx] || '' : '',
      tags: tagsIdx >= 0 ? cleanCols[tagsIdx] || '' : '',
      grupo: groupIdx >= 0 ? cleanCols[groupIdx] || '' : '',
      _raw: line,
    });
  }
  return rows;
}

export default function ImportModal({ isOpen, onClose, onImport, existingContacts, groups }: ImportModalProps) {
  const [step, setStep] = useState<ImportStep>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [validations, setValidations] = useState<ValidationResult[]>([]);
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);

  // Config options
  const [removeDuplicates, setRemoveDuplicates] = useState(true);
  const [validateWhatsApp, setValidateWhatsApp] = useState(true);
  const [defaultGroup, setDefaultGroup] = useState('');
  const [defaultTags, setDefaultTags] = useState('');
  const [ddd, setDdd] = useState(DEFAULT_DDD);
  const [importedCount, setImportedCount] = useState(0);
  const [importProgress, setImportProgress] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setStep('upload');
    setParsedRows([]);
    setValidations([]);
    setFileName('');
    setDragOver(false);
    setRemoveDuplicates(true);
    setValidateWhatsApp(true);
    setDefaultGroup('');
    setDefaultTags('');
    setDdd(DEFAULT_DDD);
    setImportedCount(0);
    setImportProgress(0);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const processFile = (file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const rows = parseCSV(text);
      setParsedRows(rows);

      if (rows.length === 0) {
        setStep('upload');
        return;
      }

      // Build validation results (countryCode will be applied at import time, validate format only)
      const existingPhones = new Set(
        existingContacts.map((c) => normalizeBrazilianPhone(c.phone, ddd))
      );

      const results: ValidationResult[] = rows.map((row, idx) => {
        const errors: string[] = [];
        if (!row.nome.trim()) errors.push('Nome vazio');
        if (!row.telefone.trim()) {
          errors.push('Telefone vazio');
        } else {
          const valid = isValidBrazilianPhone(row.telefone) || isValidInternationalPhone(row.telefone);
          if (!valid) errors.push('Número inválido');
        }
        const phoneNormalized = normalizeBrazilianPhone(row.telefone, ddd);
        const isDuplicate = existingPhones.has(phoneNormalized) ||
          rows.slice(0, idx).some((r) => normalizeBrazilianPhone(r.telefone, ddd) === phoneNormalized);

        return { row, index: idx, valid: errors.length === 0, errors, isDuplicate };
      });

      setValidations(results);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.name.endsWith('.txt'))) {
      processFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleStartImport = () => {
    setStep('importing');
    setImportProgress(0);

    let filtered = [...validations];

    // Filter invalid if validating WhatsApp
    if (validateWhatsApp) {
      filtered = filtered.filter((v) => v.valid);
    }

    // Remove duplicates
    if (removeDuplicates) {
      const seen = new Set<string>();
      filtered = filtered.filter((v) => {
        const phone = normalizeBrazilianPhone(v.row.telefone, ddd);
        if (seen.has(phone)) return false;
        seen.add(phone);
        return true;
      });
    }

    const total = filtered.length;

    // Simulate progress for UX
    let current = 0;
    const interval = setInterval(() => {
      current += Math.max(1, Math.floor(total / 10));
      if (current >= total) {
        current = total;
        clearInterval(interval);
      }
      setImportProgress(Math.round((current / total) * 100));
    }, 150);

    // Build contacts
    const newContacts: Contact[] = [];
    const now = new Date().toISOString();

    for (const v of filtered) {
      // Determine group
      let groupId = defaultGroup || null;
      if (!groupId && v.row.grupo) {
        const found = groups.find(
          (g) => g.name.toLowerCase() === v.row.grupo.toLowerCase()
        );
        if (found) groupId = found.id;
      }

      // Merge tags
      const rowTags = v.row.tags
        ? v.row.tags.split(/[,;]/).map((t) => t.trim()).filter(Boolean)
        : [];
      const configTags = defaultTags
        ? defaultTags.split(/[,;]/).map((t) => t.trim()).filter(Boolean)
        : [];
      const allTags = [...new Set([...rowTags, ...configTags])];

      newContacts.push({
        id: `imp_${Date.now()}_${v.index}`,
        name: v.row.nome.trim(),
        phone: normalizeBrazilianPhone(v.row.telefone, ddd),
        email: v.row.email.trim() || null,
        company_name: v.row.empresa.trim() || null,
        city: v.row.cidade.trim() || null,
        state: v.row.estado.trim() || null,
        tags: allTags,
        notes: null,
        group_id: groupId,
        created_at: now,
      });
    }

    setTimeout(async () => {
      try {
        await onImport(newContacts);
        setImportedCount(newContacts.length);
        setStep('done');
      } catch {
        // Error already handled by parent via toast, just go to done with 0
        setImportedCount(0);
        setStep('done');
      }
      clearInterval(interval);
      setImportProgress(100);
    }, 600);
  };

  if (!isOpen) return null;

  const validCount = validations.filter((v) => v.valid).length;
  const invalidCount = validations.filter((v) => !v.valid).length;
  const duplicateCount = validations.filter((v) => v.isDuplicate).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 w-full max-w-[680px] max-h-[85vh] mx-4 flex flex-col animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100 dark:border-background-200 flex-shrink-0">
          <div>
            <h3 className="text-base font-heading font-semibold text-foreground-950 dark:text-foreground-100">
              Importar Contatos
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              {step === 'upload' && 'Selecione um arquivo CSV com seus contatos'}
              {step === 'preview' && `${parsedRows.length} linhas detectadas — revise antes de importar`}
              {step === 'config' && 'Configure as opções de importação'}
              {step === 'importing' && 'Importando contatos...'}
              {step === 'done' && 'Importação concluída!'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 hover:text-foreground-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* ── Step: Upload ── */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-all cursor-pointer ${
                  dragOver
                    ? 'border-primary-400 bg-primary-50/50 dark:bg-primary-500/8'
                    : 'border-background-200 dark:border-background-300 hover:border-primary-300 hover:bg-primary-50/30 dark:hover:bg-primary-500/5'
                }`}
              >
                <i className={`text-4xl mb-3 block ${dragOver ? 'text-primary-500' : 'text-primary-400'}`}>
                  {dragOver ? 'ri-file-download-line' : 'ri-upload-cloud-2-line'}
                </i>
                <p className="text-sm font-medium text-foreground-700 dark:text-foreground-300">
                  {dragOver ? 'Solte o arquivo aqui' : 'Arraste um arquivo CSV aqui'}
                </p>
                <p className="text-xs text-foreground-400 mt-1">Ou clique para selecionar</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              <div className="bg-background-50 dark:bg-background-200 rounded-xl p-4 border border-background-100 dark:border-background-300">
                <p className="text-xs font-semibold text-foreground-700 dark:text-foreground-300 mb-3">
                  <i className="ri-information-line mr-1 text-primary-500" />
                  Formato esperado do CSV:
                </p>
                <div className="bg-white dark:bg-background-100 rounded-lg border border-background-200 dark:border-background-300 p-3">
                  <code className="text-xs text-foreground-600 dark:text-foreground-400 font-mono block leading-relaxed">
                    nome,telefone,email,empresa,cidade,estado,tags,grupo<br />
                    João Silva,55119987654321,joao@email.com,Acme Tech,São Paulo,SP,"vip,ativo",Clientes<br />
                    Maria Santos,5521912345678,maria@email.com,,Rio de Janeiro,RJ,lead,Leads
                  </code>
                </div>
                <div className="mt-3 space-y-1">
                  <p className="text-[11px] text-foreground-500">
                    <span className="font-semibold text-foreground-600">nome</span> e{' '}
                    <span className="font-semibold text-foreground-600">telefone</span> são obrigatórios
                  </p>
                  <p className="text-[11px] text-foreground-500">
                    Use vírgula ou ponto-e-vírgula como separador. Campos com vírgula devem estar entre aspas.
                  </p>
                  <p className="text-[11px] text-foreground-500">
                    O sistema detecta automaticamente os cabeçalhos (nome, telefone, email, empresa, cidade, estado, tags, grupo).
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Step: Preview ── */}
          {step === 'preview' && (
            <div className="space-y-5">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-background-50 dark:bg-background-200 rounded-xl p-3 text-center border border-background-100 dark:border-background-300">
                  <p className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100">{parsedRows.length}</p>
                  <p className="text-[10px] text-foreground-500">Total de linhas</p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-500/5 rounded-xl p-3 text-center border border-emerald-100 dark:border-emerald-500/10">
                  <p className="text-xl font-heading font-bold text-emerald-700 dark:text-emerald-400">{validCount}</p>
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400">Válidos</p>
                </div>
                <div className="bg-red-50 dark:bg-red-500/5 rounded-xl p-3 text-center border border-red-100 dark:border-red-500/10">
                  <p className="text-xl font-heading font-bold text-red-700 dark:text-red-400">{invalidCount}</p>
                  <p className="text-[10px] text-red-600 dark:text-red-400">Inválidos</p>
                </div>
                <div className="bg-amber-50 dark:bg-amber-500/5 rounded-xl p-3 text-center border border-amber-100 dark:border-amber-500/10">
                  <p className="text-xl font-heading font-bold text-amber-700 dark:text-amber-400">{duplicateCount}</p>
                  <p className="text-[10px] text-amber-600 dark:text-amber-400">Duplicados</p>
                </div>
              </div>

              {/* Table preview (first 8 rows) */}
              <div className="bg-white dark:bg-background-100 rounded-xl border border-background-200 dark:border-background-300 overflow-hidden">
                <div className="overflow-x-auto max-h-[280px]">
                  <table className="w-full text-xs">
                    <thead className="bg-background-50 dark:bg-background-200 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold text-foreground-500 uppercase">#</th>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold text-foreground-500 uppercase">Nome</th>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold text-foreground-500 uppercase">Telefone</th>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold text-foreground-500 uppercase hidden sm:table-cell">Email</th>
                        <th className="px-3 py-2 text-left text-[10px] font-semibold text-foreground-500 uppercase hidden sm:table-cell">Grupo</th>
                        <th className="px-3 py-2 text-center text-[10px] font-semibold text-foreground-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validations.slice(0, 8).map((v) => (
                        <tr
                          key={v.index}
                          className={`border-b border-background-50 dark:border-background-200 ${
                            !v.valid ? 'bg-red-50/30 dark:bg-red-500/3' : v.isDuplicate ? 'bg-amber-50/30 dark:bg-amber-500/3' : ''
                          }`}
                        >
                          <td className="px-3 py-2 text-foreground-400">{v.index + 1}</td>
                          <td className="px-3 py-2 font-medium text-foreground-800 dark:text-foreground-200 truncate max-w-[120px]">
                            {v.row.nome || <span className="text-foreground-400 italic">vazio</span>}
                          </td>
                          <td className="px-3 py-2 text-foreground-600 dark:text-foreground-400 font-mono tabular-nums">
                            {v.row.telefone || <span className="text-foreground-400 italic">vazio</span>}
                          </td>
                          <td className="px-3 py-2 text-foreground-500 hidden sm:table-cell truncate max-w-[140px]">
                            {v.row.email || '—'}
                          </td>
                          <td className="px-3 py-2 text-foreground-500 hidden sm:table-cell">
                            {v.row.grupo || '—'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {!v.valid ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-600 dark:text-red-400" title={v.errors.join(', ')}>
                                <i className="ri-close-circle-line" />{v.errors[0]}
                              </span>
                            ) : v.isDuplicate ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                                <i className="ri-contrast-2-line" />Duplicado
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                                <i className="ri-check-line" />OK
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {validations.length > 8 && (
                  <div className="px-3 py-2 text-center text-[11px] text-foreground-400 bg-background-50 dark:bg-background-200 border-t border-background-100 dark:border-background-300">
                    + mais {validations.length - 8} linha{validations.length - 8 > 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={reset}
                  className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-1" />
                  Arquivo diferente
                </button>
                <button
                  onClick={() => setStep('config')}
                  disabled={parsedRows.length === 0}
                  className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-40 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer ml-auto"
                >
                  Continuar
                  <i className="ri-arrow-right-line ml-1" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step: Config ── */}
          {step === 'config' && (
            <div className="space-y-5">
              <div className="bg-background-50 dark:bg-background-200 rounded-xl p-4 border border-background-100 dark:border-background-300 space-y-4">
                {/* Remove duplicates toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">
                      <i className="ri-contrast-2-line mr-1.5 text-amber-500" />
                      Remover duplicados
                    </p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {duplicateCount} número{duplicateCount !== 1 ? 's' : ''} duplicado{duplicateCount !== 1 ? 's' : ''} detectado{duplicateCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={removeDuplicates}
                    onClick={() => setRemoveDuplicates(!removeDuplicates)}
                    className={`relative w-10 h-5.5 rounded-full transition-colors cursor-pointer ${
                      removeDuplicates ? 'bg-primary-500' : 'bg-background-300 dark:bg-background-400'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow-sm transition-transform ${
                        removeDuplicates ? 'translate-x-[18px]' : 'translate-x-[2px]'
                      }`}
                    />
                  </button>
                </div>

                {/* Validate WhatsApp toggle */}
                <div className="flex items-center justify-between pt-3 border-t border-background-200 dark:border-background-300">
                  <div>
                    <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">
                      <i className="ri-shield-check-line mr-1.5 text-emerald-500" />
                      Validar WhatsApp
                    </p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {invalidCount} número{invalidCount !== 1 ? 's' : ''} inválido{invalidCount !== 1 ? 's' : ''} — serão ignorados na importação
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={validateWhatsApp}
                    onClick={() => setValidateWhatsApp(!validateWhatsApp)}
                    className={`relative w-10 h-5.5 rounded-full transition-colors cursor-pointer ${
                      validateWhatsApp ? 'bg-primary-500' : 'bg-background-300 dark:bg-background-400'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow-sm transition-transform ${
                        validateWhatsApp ? 'translate-x-[18px]' : 'translate-x-[2px]'
                      }`}
                    />
                  </button>
                </div>

                {/* DDD selector */}
                <div className="pt-3 border-t border-background-200 dark:border-background-300">
                  <label className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-2">
                    <i className="ri-map-pin-line mr-1.5" />
                    DDD (Código de Área) do Brasil
                  </label>
                  <select
                    value={ddd}
                    onChange={(e) => setDdd(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-900 dark:text-foreground-100 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  >
                    {BRAZIL_DDDS.map((d) => (
                      <option key={d.code} value={d.code}>
                        {d.code} — {d.state} ({d.region})
                      </option>
                    ))}
                    <option value="">Nenhum (manter como está)</option>
                  </select>
                  <p className="text-[10px] text-foreground-400 mt-1.5">
                    {ddd
                      ? <span>Números sem DDD receberão <strong>55 (Brasil) + {ddd}</strong> automaticamente. Números com DDD mas sem código de país receberão <strong>55</strong>.</span>
                      : <span>Os números serão importados exatamente como estão no CSV</span>}
                  </p>
                </div>

                {/* Default group */}
                <div className="pt-3 border-t border-background-200 dark:border-background-300">
                  <label className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-2">
                    <i className="ri-group-line mr-1.5" />
                    Grupo padrão (opcional)
                  </label>
                  <select
                    value={defaultGroup}
                    onChange={(e) => setDefaultGroup(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-900 dark:text-foreground-100 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  >
                    <option value="">— Não atribuir grupo —</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-foreground-400 mt-1.5">
                    Se o CSV tiver a coluna "grupo", ela terá prioridade sobre esta configuração.
                  </p>
                </div>

                {/* Default tags */}
                <div className="pt-3 border-t border-background-200 dark:border-background-300">
                  <label className="block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-2">
                    <i className="ri-price-tag-3-line mr-1.5" />
                    Tags padrão (opcional)
                  </label>
                  <input
                    type="text"
                    value={defaultTags}
                    onChange={(e) => setDefaultTags(e.target.value)}
                    placeholder="Ex: importado, campanha-verão, 2026"
                    className="w-full px-3 py-2 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-900 dark:text-foreground-100 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                  <p className="text-[10px] text-foreground-400 mt-1.5">
                    Separe múltiplas tags com vírgula. Tags do CSV serão mescladas com estas.
                  </p>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-primary-50 dark:bg-primary-500/5 border border-primary-200 dark:border-primary-500/10 rounded-xl p-4">
                <p className="text-sm font-semibold text-primary-700 dark:text-primary-400 mb-1">
                  Resumo da importação
                </p>
                <p className="text-xs text-primary-600 dark:text-primary-500">
                  {(() => {
                    const count = (validateWhatsApp ? validCount : parsedRows.length) - (removeDuplicates ? duplicateCount : 0);
                    const s = count !== 1 ? 's' : '';
                    let extras = '';
                    if (defaultGroup) {
                      const g = groups.find((gr) => gr.id === defaultGroup);
                      extras += ' · Grupo: ' + (g?.name || defaultGroup);
                    }
                    if (defaultTags) extras += ' · Tags: ' + defaultTags;
                    if (ddd) extras += ' · Prefixo: 55 (Brasil) + DDD ' + ddd;
                    return `${count} contato${s} serão importado${s}${extras}`;
                  })()}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setStep('preview')}
                  className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-1" />
                  Voltar
                </button>
                <button
                  onClick={handleStartImport}
                  className="px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer ml-auto flex items-center gap-2"
                >
                  <i className="ri-file-upload-line" />
                  Iniciar importação
                </button>
              </div>
            </div>
          )}

          {/* ── Step: Importing ── */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-16 h-16 rounded-full bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center mb-5 animate-pulse">
                <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin" />
              </div>
              <h4 className="text-sm font-semibold text-foreground-900 dark:text-foreground-100 mb-2">
                Importando contatos...
              </h4>
              <div className="w-full max-w-[300px] h-2.5 bg-background-100 dark:bg-background-200 rounded-full overflow-hidden mb-3">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all duration-300"
                  style={{ width: `${importProgress}%` }}
                />
              </div>
              <p className="text-xs text-foreground-500">{importProgress}% concluído</p>
            </div>
          )}

          {/* ── Step: Done ── */}
          {step === 'done' && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center mb-5">
                <i className="ri-check-line text-3xl text-emerald-500" />
              </div>
              <h4 className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">
                Importação concluída!
              </h4>
              <p className="text-sm text-foreground-500 mb-2">
                <strong className="text-foreground-800 dark:text-foreground-200">{importedCount}</strong> contato{importedCount !== 1 ? 's' : ''} importado{importedCount !== 1 ? 's' : ''} com sucesso.
              </p>
              {invalidCount > 0 && validateWhatsApp && (
                <p className="text-xs text-foreground-400 mb-1">
                  {invalidCount} número{invalidCount !== 1 ? 's' : ''} inválido{invalidCount !== 1 ? 's' : ''} ignorado{invalidCount !== 1 ? 's' : ''}.
                </p>
              )}
              {duplicateCount > 0 && removeDuplicates && (
                <p className="text-xs text-foreground-400 mb-6">
                  {duplicateCount} duplicado{duplicateCount !== 1 ? 's' : ''} removido{duplicateCount !== 1 ? 's' : ''}.
                </p>
              )}
              <button
                onClick={handleClose}
                className="mt-4 px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line mr-1" />
                Concluído
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}